import "core-js/stable";
import "regenerator-runtime/runtime";
import Vue from "vue"
import AppRouter from "@/router"
import Vuex from 'vuex'
import VuexStore from './store'
import App from "@/App.vue"

import {
    APIService,
    AuthService,
    AuditService,
    API_ERROR_TYPE_NORMAL,
    API_ERROR_TYPE_HIDDEN,
    API_ERROR_TYPE_REDIRECT,
    http_build_query,
    trimSlashes
} from '@utils'

import OCVueFramework from '@framework'
import {OCFlashMessagesPlugin, EventBus} from '@framework'
import Captcha from '@/widgets/captcha'
import AuthHelper from '@/pages/auth/helpers/AuthHelper'

///////////// PLUGINS //////////////

// !!! use these while developing a plugin && need to be before app vue instance
// import ExamplePlugin from "./plugins/example/Plugin"
// Vue.use(ExamplePlugin, {router: AppRouter, AuthHelper})

for( let i in _appConfig.plugins ) {
    let plugin = _appConfig.plugins[i]
    if( window[plugin] === undefined ) {
        console.error(`Invalid plugin "${plugin}"`)
        continue
    }
    Vue.use(window[plugin].default, {router: AppRouter, AuthHelper})
}

////////////////////////////////////

import '@/assets/js/app.js'
import '@/assets/css/app.css'

Vue.component(Captcha.name, Captcha)

Vue.use(Vuex)

const axiosInstance = axios.create({
    withCredentials: true, // allow session cookie for the CORS supported domain
    baseURL: _appConfig.publicApiURL,
    timeout: 60000,
    headers: {
        common: _appConfig.publicApiURLHeaders
    },
    validateStatus: (status) => {
        return true
    }
})

window.axiosInstance = axiosInstance

let requestsCounter = 0

let APICallbacks = {
    onBeforeResponseValidation: function (response) {
        if(response && response.data) {
            return response.data
        }
    },
    onBeforeRequest: function (requestParams) {
        if( requestParams.options.showLoader === false ) {
            return
        }

        if( ! (requestParams.api === "login" && requestParams.method === "sign-in") ) { // we display a different loader
            EventBus.$emit('show-loader')
        }

        requestsCounter++
    },
    onAfterRequest: function (requestParams, response, error) {
        if( requestParams.options.showLoader === false ) {
            return
        }

        requestsCounter--
        if(requestsCounter <= 0) {
            EventBus.$emit('hide-loader')
            requestsCounter = 0
        }
    },
    onErrorNotifyCallback: function (APIServiceException) {
        if( _appConfig.mode === 'dev' ) {
            app.$addFlashMessage(APIServiceException.getMessage(), 'error')
        }
        else if ( API_ERROR_TYPE_NORMAL === APIServiceException.getType() ) {
            app.$addFlashMessage(APIServiceException.getMessage(), 'error')
        }
        else if (
            API_ERROR_TYPE_HIDDEN === APIServiceException.getType() ||
            API_ERROR_TYPE_REDIRECT === APIServiceException.getType()
        ) {
            // do nothing
        } else {
            app.$addFlashMessage('Operation failed. If the problem persists please contact your vendor.', 'error')
            console.error(APIServiceException.getMessage())
        }
    },
    onRedirect: function (response, requestParams, sender) {

        if ( _appConfig.mode === 'prod' ) {
            window.location.href = response.url
        }

        for (let type in response.flashMessages)
        {
            app.$addFlashMessage(response.flashMessages[type], 'error')
        }
    }
}

window.REST = new APIService(
    function fetch (requestUrl, params, headersJS, encodeRequestParams, requestParams) {

        let headers = (undefined !== requestParams.options.headers)
            ? requestParams.options.headers
            : {}

        headers = Object.assign({
            'X-Oc-Api-Request': "1", // value need to be string
        }, headersJS, headers)

        // -----------------

        let method = requestParams.type.toLowerCase()

        let requestData = cloneObject(params)

        let queryParams = cloneObject(requestParams.queryParams)

        if( method === 'get' )
        {
            queryParams = Object.assign(queryParams, params)
            requestData = {}
        }

        requestData = encodeRequestParams ? Qs.stringify(requestData) : requestData

        return axiosInstance({
            baseURL: _appConfig.publicURL,
            method,
            url: requestUrl,
            params: queryParams,
            data: requestData,
            headers
        })
    },
    APICallbacks.onBeforeResponseValidation,
    APICallbacks.onBeforeRequest,
    APICallbacks.onAfterRequest,
    APICallbacks.onErrorNotifyCallback,
    function onRedirect (response, requestParams) {
        return APICallbacks.onRedirect(response, requestParams, REST)
    },
    function prepareUrl ({ api, method, type, options, queryParams }) {
        return new Promise((resolve, reject) => {
            if( isEmpty(api) ) {
                return reject(new APIServiceError('[RequestService] Parameter "api" of RequestService::request() is required!'))
            }

            let requestUrl = '/' + trimSlashes(api)

            if( ! isEmpty(method) ) {
                requestUrl += '/' + trimSlashes(method)
            }

            resolve(requestUrl)
        })
    }
);

window.API = new APIService(
    function fetch (requestUrl, params, headersJS, encodeRequestParams) {
        let jsonParams = {
            '--api-params--': toJson(params)
        }

        if( Object.keys(_appConfig.publicApiURLParams).length > 0 )
        {
            requestUrl += requestUrl.indexOf('?') === -1 ? '?' : '&'
            requestUrl += http_build_query(_appConfig.publicApiURLParams)
        }

        return axiosInstance.post(
            requestUrl,
            encodeRequestParams ? Qs.stringify(jsonParams) : jsonParams,
            {
                headers: headersJS
            }
        )
    },
    APICallbacks.onBeforeResponseValidation,
    APICallbacks.onBeforeRequest,
    function onAfterRequest (requestParams, response, error) {
        APICallbacks.onAfterRequest(requestParams, response, error);

        // handle login success and plugin redirects
        AuthHelper.handleSpecialResponse(response)
    },
    APICallbacks.onErrorNotifyCallback,
    function onRedirect (response, requestParams) {
        return APICallbacks.onRedirect(response, requestParams, API)
    }
)

window.Auth = new AuthService(
    window.API,
    _appConfig.ownershipExpireTime, // in minutes
    _appConfig.accountInfo.permissions,
    _appConfig.accountInfo.groups,
    _appConfig.system + '.' + _appConfig.name, // base root
    _appConfig.mode === 'dev' || _appConfig.debug // refreshOnInit
)

window.AuditHelper = new AuditService(
    window.API,
    'audit-service',
    'log'
)

Vue.use(OCVueFramework, {
    settings: _appConfig.settings,
    auth: {
        checkPermission: (permission) => {
            return Auth.has(permission)
        }
    },
    components: {
        'oc-button': {
            size: 'small'
        },
        'oc-page': {
            titleFormat: (title) => {
                return _appConfig.metaTitlePrefix + ' - ' + _appConfig.title + ': ' + title
            }
        }
    }
})

window.app = new Vue({
    router: AppRouter,
    store: VuexStore,
    template: "<App />",
    data: {
        auth: window.Auth, // this is done in order to activate the Vue reactivity for the AuthService
        customerLogo: _appConfig.client.logo,
        checkAuthStateInterval: undefined
    },
    components: {
        App
    },
    beforeCreate() {
        this.$store.commit('auth/INFO', {
            username: _appConfig.auth.username,
            users: _appConfig.auth.users
        })
    },
    methods: {
        logout() {
            window.location.href = _appConfig.publicURL + 'oauth/logout?embed=' + _appConfig.embed
            window._appRedirected = true // prevent the next router actions
        },
        debug(msg) {
            if( _appConfig.mode === 'dev' ) {
                w(msg)
            }
        }
    },
    computed: {
        isGuest() {
            return this.$store.getters['auth/isGuest']
        },
        authInfo() {
            return this.$store.getters['auth/getInfo']
        },
        isClient() {
            return !isEmpty(_appConfig.client.client_id)
        },
        embed() {
            return _appConfig.embed === 1
        },
        debug() {
            return _appConfig.debug
        },
        publicURL() {
            if( _appConfig.mode === 'dev' ) {
                return '/'
            }

            return _appConfig.publicURL
        }
    }
})

Vue.use({
    install: function (Vue, options) {

        Vue.prototype.$auth = window.Auth

        Vue.prototype.$breadcrumb = function (list = [], addAppTitle = true, separator = '/') {
            let resultList = []
            list = list.map(item => Object.assign(item, {type: 'link'}))
            list.forEach((item, index) => {
                if (item.to) item.to = '#' + item.to

                if (index === 0 && addAppTitle) resultList.push({type: 'app-title', title: window._appConfig.title})

                if (index !== 0 && index !== list.length) resultList.push({type: 'separator', separator})

                resultList.push(item)
            })

            window.top.postMessage({type: 'breadcrumb', items: resultList}, "*");
        }
    }
})

Vue.use(OCFlashMessagesPlugin, {router: AppRouter})

app.$mount('#app')
